"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { track } from "@lib/track";

type Path = "discovery" | "consultation" | "farleigh" | "bodhi";

const QUESTIONS = [
  {
    text: "What's your biggest health goal right now?",
    options: [
      { label: "Boost energy, sleep or mood", scores: { farleigh: 1, bodhi: 1 } },
      { label: "Address weight, digestion or hormones", scores: { consultation: 1, farleigh: 1 } },
      { label: "Understand a chronic or long-standing condition", scores: { consultation: 2 } },
      { label: "Not sure yet — I just want to feel better", scores: { discovery: 2 } },
    ],
  },
  {
    text: "What kind of support feels right?",
    options: [
      { label: "A personal deep-dive with a specialist", scores: { consultation: 2 } },
      { label: "A structured, step-by-step programme", scores: { farleigh: 2 } },
      { label: "Monthly expert guidance and community", scores: { bodhi: 2 } },
      { label: "A friendly, no-pressure chat first", scores: { discovery: 2 } },
    ],
  },
  {
    text: "How ready are you to invest in your health?",
    options: [
      { label: "Just exploring for now", scores: { discovery: 2 } },
      { label: "Open to a manageable monthly commitment", scores: { bodhi: 2 } },
      { label: "Ready to make a meaningful investment", scores: { consultation: 1, farleigh: 1 } },
      { label: "Whatever it takes for real, lasting results", scores: { consultation: 2 } },
    ],
  },
];

const RESULTS: Record<
  Path,
  { emoji: string; title: string; tagline: string; cta: string; href: string; charge: string; highlights: string[]; badge?: string }
> = {
  discovery: {
    emoji: "☕",
    title: "Start with a Free Discovery Call",
    tagline: "A calm 15-minute chat with Niki — zero pressure, total clarity.",
    cta: "Book My Free Call",
    href: "/workwithme/discoverycall",
    charge: "Free",
    highlights: ["15 minutes, no obligation", "Understand all your options", "Led personally by Niki"],
  },
  farleigh: {
    emoji: "🧭",
    title: "The Farleigh Method",
    tagline: "A structured protocol that tracks what changes and adapts as you go.",
    cta: "Explore the Farleigh Method",
    href: "/workwithme/farleighMethod",
    charge: "From £450",
    highlights: ["Systematic — one change at a time", "Root-cause focused", "Clear, measurable milestones"],
    badge: "Signature Programme",
  },
  consultation: {
    emoji: "✨",
    title: "Personalised Consultation",
    tagline: "A full 60-minute deep-dive — root causes found, a real plan made.",
    cta: "Book a Consultation",
    href: "/workwithme/consultation",
    charge: "From £250",
    highlights: ["60-min 1-to-1 with Niki", "Full health history review", "Personalised nutrition plan"],
  },
  bodhi: {
    emoji: "🌿",
    title: "Bodhi Health Club",
    tagline: "Monthly expert guidance, community and accountability — from £40.",
    cta: "Join the Bodhi Club",
    href: "/workwithme/align",
    charge: "£40/month",
    highlights: ["Monthly 1-to-1 with Niki", "Group coaching sessions", "Flexible — cancel anytime"],
  },
};

const GOALS = [
  { icon: "⚡", label: "Energy & fatigue",     href: "/workwithme/discoverycall" },
  { icon: "🌙", label: "Sleep & stress",        href: "/workwithme/discoverycall" },
  { icon: "🫶", label: "Gut & digestion",       href: "/workwithme/consultation" },
  { icon: "⚖️", label: "Weight & hormones",     href: "/workwithme/consultation" },
  { icon: "🧠", label: "Brain fog & mood",      href: "/workwithme/discoverycall" },
  { icon: "🤱", label: "Fertility & pregnancy", href: "/workwithme/consultation" },
  { icon: "👶", label: "Children's health",     href: "/workwithme/consultation" },
  { icon: "❤️", label: "Chronic conditions",    href: "/workwithme/consultation" },
];

const TESTIMONIALS = [
  {
    quote: "I've never felt so well, so grounded, and so in tune with myself.",
    body: "Nichola helped me detox and reclaim my health. I'm nearly medication-free and feel emotionally and spiritually stronger than ever.",
    name: "Lucie W.",
    role: "Wellness Programme",
  },
  {
    quote: "Niki's passion and enthusiasm is infectious.",
    body: "Her patience in answering any questions is endless. Everything we learned was accessible and simple to continue at home.",
    name: "Jayne G.",
    role: "Retreat Guest",
  },
  {
    quote: "Every bit of advice from Niki has made me feel healthier.",
    body: "The gut cleanse was unreal — I felt so much better afterwards, and losing a few kilos was a great side note.",
    name: "Helen.",
    role: "Gut Cleanse Client",
  },
  {
    quote: "A Ctrl-Alt-Del for body, mind and soul.",
    body: "Mentally, the programme took me to my edge — removing caffeine and sugar was both tough AND so worth doing. I felt sharper, more focused, positive and more resilient.",
    name: "Claire D.",
    role: "Heavy Metal Cleanse",
  },
];

const FAQ_ITEMS = [
  {
    q: "What actually happens in a consultation?",
    a: "Niki spends the full 60 minutes on you — your health history, symptoms, diet, lifestyle and goals. She identifies root causes, not just symptoms, and sends you a written personalised plan within a few days. No rushed GP-style appointments.",
  },
  {
    q: "Is this compatible with my GP or specialist?",
    a: "Yes. Naturopathic nutrition is complementary, not a replacement for medical care. Niki works alongside conventional treatment and will always refer you on if something needs medical investigation.",
  },
  {
    q: "Do you offer payment plans?",
    a: "Yes — payment plans are available for the Farleigh Method and consultation packages. Get in touch after booking or mention it on your discovery call.",
  },
  {
    q: "How do I know which option is right for me?",
    a: "The 3-question quiz above is the fastest way to find out. Or book a free discovery call — it's a no-pressure 15-minute chat where Niki will tell you exactly what she'd recommend for your situation.",
  },
];

function computeResult(answers: number[][]): Path {
  const totals: Record<Path, number> = { discovery: 0, consultation: 0, farleigh: 0, bodhi: 0 };
  answers.forEach((selected, qIdx) => {
    selected.forEach((optIdx) => {
      const scores = QUESTIONS[qIdx].options[optIdx].scores as Partial<Record<Path, number>>;
      (Object.keys(scores) as Path[]).forEach((p) => { totals[p] += scores[p] ?? 0; });
    });
  });
  return (Object.keys(totals) as Path[]).reduce((best, p) =>
    totals[p as Path] > totals[best] ? (p as Path) : best
  ) as Path;
}

export default function WorkWithMeClient() {
  const [quizStep, setQuizStep] = useState<"idle" | "quiz" | "result">("idle");
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<number[][]>(Array(QUESTIONS.length).fill([]));
  const [selected, setSelected] = useState<number | null>(null);
  const [result, setResult] = useState<Path | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  function startQuiz() {
    track("quiz_start", { label: "start", component: "WorkWithMePage" }, { event: "quiz_start", label: "start", component: "WorkWithMePage" });
    setQuizStep("quiz");
    setCurrent(0);
    setAnswers(Array(QUESTIONS.length).fill([]));
    setSelected(null);
    setResult(null);
  }

  function handleNext() {
    if (selected === null) return;
    const newAnswers = answers.map((a, i) => (i === current ? [selected] : a));
    setAnswers(newAnswers);
    track(
      "quiz_answer",
      { label: `Q${current + 1}:A${selected}`, component: "WorkWithMePage" },
      { event: "quiz_answer", label: `Q${current + 1}:A${selected}`, component: "WorkWithMePage", meta: { question: current + 1, answer: selected } }
    );
    if (current < QUESTIONS.length - 1) {
      setCurrent(current + 1);
      setSelected(null);
    } else {
      const path = computeResult(newAnswers);
      setResult(path);
      setQuizStep("result");
      track("quiz_complete", { label: path, component: "WorkWithMePage" }, { event: "quiz_complete", label: path, component: "WorkWithMePage", meta: { result: path } });
    }
  }

  function handleBack() {
    if (current === 0) { setQuizStep("idle"); return; }
    setCurrent(c => c - 1);
    setSelected(answers[current - 1]?.[0] ?? null);
  }

  const progress = Math.round(((current + 1) / QUESTIONS.length) * 100);

  return (
    <div className="min-h-screen">

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="relative bg-[var(--color-brand)] text-[var(--text-on-dark)] overflow-hidden">
        <div className="mx-auto max-w-5xl px-5 md:px-10 pt-14 md:pt-20 grid md:grid-cols-2 gap-10 items-end">
          <div className="pb-14 md:pb-20">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-[var(--text-heading)] mb-4">
              Naturopathic Nutrition · Southport, Manchester &amp; Online
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight text-balance mb-5">
              Still searching for why you don&apos;t feel well?
            </h1>
            <p className="text-base md:text-lg opacity-85 mb-8 max-w-md leading-relaxed">
              Niki Charnock finds the root cause — not just the symptoms. Find your path in 60 seconds.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={startQuiz}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--btn-inverse-bg)] text-[var(--btn-inverse-text)] font-bold text-base px-8 py-4 shadow-lg hover:bg-[var(--btn-inverse-hover)] transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Find My Path →
              </button>
              <Link
                href="/workwithme/discoverycall"
                onClick={() => track("click", { label: "Hero - Book Free Call", component: "WorkWithMePage" }, { event: "click", label: "Hero - Book Free Call", component: "WorkWithMePage" })}
                className="inline-flex items-center justify-center rounded-full border border-[var(--border-on-dark)] text-[var(--text-on-dark)] font-semibold text-base px-8 py-4 hover:border-[var(--text-on-dark)] hover:bg-white/10 transition-all"
              >
                Book Free Call
              </Link>
            </div>
            <div className="flex flex-wrap gap-5 mt-8 text-xs text-[var(--text-on-dark-faint)]">
              <span>✓ 100% personalised</span>
              <span>✓ Root-cause focused</span>
              <span>✓ Free discovery call available</span>
            </div>
          </div>

          <div className="hidden md:flex justify-end items-end">
            <img
              src="/images/Niki_Charnock.webp"
              alt="Niki Charnock, Naturopathic Nutritionist"
              className="h-[400px] w-auto object-cover object-top rounded-t-3xl"
            />
          </div>
        </div>
      </section>

      {/* ── INLINE QUIZ ───────────────────────────────────────────────── */}
      <section id="quiz" className="bg-[var(--bg-page)] border-b border-[var(--border-on-light)]">
        <div className="mx-auto max-w-2xl px-5 py-14 md:py-20 pb-28 md:pb-20">

          {quizStep === "idle" && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <p className="text-xs font-semibold text-[var(--text-on-light-faint)] uppercase tracking-wider mb-3">Not sure where to start?</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-brand)] mb-3">
                Find your perfect fit in 3 questions
              </h2>
              <p className="text-[var(--text-on-light-muted)] text-sm mb-8">Takes 60 seconds. No email required.</p>
              <button
                onClick={startQuiz}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--color-brand)] text-[var(--text-on-dark)] font-bold px-10 py-4 shadow-md hover:opacity-90 transition-all hover:scale-[1.01]"
              >
                Start the Quiz →
              </button>
            </motion.div>
          )}

          {quizStep === "quiz" && (
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.22 }}
              >
                <div className="flex items-center gap-3 mb-8">
                  <div className="flex-1 h-1.5 bg-[var(--bg-surface-dk)] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-[var(--color-brand)] rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  <span className="text-xs text-[var(--text-on-light-faint)] shrink-0">{current + 1} / {QUESTIONS.length}</span>
                </div>

                <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] mb-6">
                  {QUESTIONS[current].text}
                </h2>

                <div className="space-y-3 mb-8">
                  {QUESTIONS[current].options.map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelected(idx)}
                      className={`w-full text-left rounded-2xl border-2 px-5 py-4 text-sm md:text-base font-medium transition-all ${
                        selected === idx
                          ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-[var(--text-on-dark)] shadow-md scale-[1.01]"
                          : "border-[var(--border-on-light)] bg-[var(--bg-card)] text-[var(--text-on-light)] hover:border-[var(--color-brand)]/50 hover:shadow-sm"
                      }`}
                    >
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold mr-3 flex-shrink-0 ${
                        selected === idx ? "bg-[var(--bg-surface)] text-[var(--color-brand)]" : "bg-[var(--bg-surface-dk)] text-[var(--text-on-light-muted)]"
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <button onClick={handleBack} className="text-sm text-[var(--text-on-light-faint)] hover:text-[var(--text-on-light-muted)] transition-colors min-h-[44px] px-2">
                    ← Back
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={selected === null}
                    className={`rounded-full font-bold px-8 py-3 text-sm transition-all ${
                      selected !== null
                        ? "bg-[var(--color-brand)] text-[var(--text-on-dark)] hover:opacity-90 hover:scale-[1.01]"
                        : "bg-[var(--bg-surface-dk)] text-[var(--text-on-light-faint)] cursor-not-allowed"
                    }`}
                  >
                    {current < QUESTIONS.length - 1 ? "Next →" : "See My Result →"}
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}

          {quizStep === "result" && result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--color-brand)]/10 border-2 border-[var(--color-brand)] text-3xl mb-4"
                >
                  {RESULTS[result].emoji}
                </motion.div>
                <div className="inline-block bg-[var(--color-brand)] text-[var(--text-on-dark)] text-xs font-bold tracking-widest uppercase rounded-full px-4 py-1.5 mb-4">
                  Your Recommendation
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-brand)] mb-2">
                  {RESULTS[result].title}
                </h2>
                <p className="text-[var(--text-on-light-muted)] text-sm max-w-sm mx-auto">{RESULTS[result].tagline}</p>
              </div>

              <div className="rounded-2xl bg-[var(--bg-card)] border border-[var(--border-on-light)] shadow-sm p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-semibold text-[var(--text-on-light-faint)] uppercase tracking-wider">What&apos;s included</p>
                  <span className="text-sm font-bold text-[var(--text-brand)]">{RESULTS[result].charge}</span>
                </div>
                <ul className="space-y-2">
                  {RESULTS[result].highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2 text-sm text-[var(--text-on-light)]">
                      <span className="text-[var(--text-brand)] font-bold">✓</span> {h}
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                href={RESULTS[result].href}
                onClick={() => track("click", { label: `Quiz Result CTA - ${result}`, component: "WorkWithMePage" }, { event: "click", label: `Quiz Result CTA - ${result}`, component: "WorkWithMePage", meta: { result } })}
                className="flex items-center justify-center gap-2 w-full bg-[var(--color-brand)] text-[var(--text-on-dark)] font-bold rounded-full py-4 mb-4 hover:opacity-90 transition-all shadow-md"
              >
                {RESULTS[result].cta} →
              </Link>

              <button
                onClick={startQuiz}
                className="w-full text-center text-xs text-[var(--text-on-light-faint)] hover:text-[var(--text-on-light-muted)] underline underline-offset-2 py-2"
              >
                Retake the quiz
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* ── GOALS ─────────────────────────────────────────────────────── */}
      <section className="bg-[var(--bg-surface)] py-16 md:py-20 px-5">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold tracking-widest uppercase text-[var(--text-heading)] mb-2">Already know your goal?</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-brand)]">What are you working on?</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {GOALS.map(({ icon, label, href }) => (
              <Link
                key={label}
                href={href}
                onClick={() => track("click", { label: `Goal card: ${label}`, component: "WorkWithMePage" }, { event: "click", label: `Goal card: ${label}`, component: "WorkWithMePage" })}
                className="flex flex-col items-center gap-2 rounded-2xl border border-[var(--border-on-light)] bg-[var(--bg-card)] p-5 text-center hover:border-[var(--color-brand)] hover:shadow-sm transition-all text-sm font-medium text-[var(--text-brand)]"
              >
                <span className="text-2xl">{icon}</span>
                {label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CHOOSE YOUR PATH ──────────────────────────────────────────── */}
      <section className="bg-[var(--bg-surface)] py-12 md:py-16 px-5 border-b border-[var(--border-on-light)]">
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-8">
            <p className="text-xs font-semibold tracking-widest uppercase text-[var(--text-heading)] mb-2">Already know what you need?</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-brand)]">Choose your path</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { arrow: "Not sure where to start?", rec: "Free Discovery Call", href: "/workwithme/discoverycall", label: "discovery" },
              { arrow: "Want structured results fast?", rec: "The Farleigh Method", href: "/workwithme/farleighMethod", label: "farleigh" },
              { arrow: "Returning or follow-up client?", rec: "Book direct below", href: "#services", label: "returning" },
            ].map(({ arrow, rec, href, label }) => (
              <Link
                key={label}
                href={href}
                onClick={() => track("click", { label: `Choose path: ${label}`, component: "WorkWithMePage" }, { event: "click", label: `Choose path: ${label}`, component: "WorkWithMePage" })}
                className="rounded-2xl border border-[var(--border-on-light)] bg-[var(--bg-card)] p-5 hover:border-[var(--color-brand)] hover:shadow-sm transition-all"
              >
                <p className="text-xs text-[var(--text-on-light-muted)] mb-1">{arrow}</p>
                <p className="font-semibold text-[var(--text-brand)] text-sm">{rec} →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ──────────────────────────────────────────────────── */}
      <section id="services" className="bg-[var(--bg-page)] py-16 md:py-20 px-5">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-widest uppercase text-[var(--text-heading)] mb-2">All pathways</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-brand)]">Choose your level of support</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {(Object.entries(RESULTS) as [Path, (typeof RESULTS)[Path]][]).map(([key, r]) => (
              <div key={key} className={`rounded-2xl bg-[var(--bg-card)] shadow-sm p-6 flex flex-col gap-4 border ${r.badge ? "border-[var(--color-brand)]" : "border-[var(--border-on-light)]"}`}>
                {r.badge && (
                  <span className="self-start text-xs font-bold tracking-widest uppercase rounded-full bg-[var(--color-brand)] text-[var(--text-on-dark)] px-3 py-1">
                    {r.badge}
                  </span>
                )}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="text-2xl">{r.emoji}</span>
                    <h3 className="mt-2 text-lg font-bold text-[var(--text-brand)]">{r.title}</h3>
                    <p className="text-sm text-[var(--text-on-light-muted)] mt-1">{r.tagline}</p>
                  </div>
                  <span className="text-sm font-bold text-[var(--text-brand)] shrink-0">{r.charge}</span>
                </div>
                <ul className="space-y-1.5">
                  {r.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2 text-sm text-[var(--text-on-light-muted)]">
                      <span className="text-[var(--text-brand)]">✓</span> {h}
                    </li>
                  ))}
                </ul>
                <Link
                  href={r.href}
                  onClick={() => track("click", { label: `Service card CTA - ${key}`, component: "WorkWithMePage" }, { event: "click", label: `Service card CTA - ${key}`, component: "WorkWithMePage" })}
                  className="mt-auto inline-flex items-center justify-center rounded-full border border-[var(--color-brand)] text-[var(--text-brand)] text-sm font-semibold px-5 py-2.5 hover:bg-[var(--color-brand)] hover:text-[var(--text-on-dark)] transition-all"
                >
                  {r.cta} →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────────────── */}
      <section className="bg-[var(--bg-surface)] py-16 md:py-20 px-5">
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-brand)]">What clients say</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="rounded-2xl border border-[var(--border-on-light)] bg-[var(--bg-card)] p-6 shadow-sm">
                <p className="text-[var(--text-brand)] font-semibold italic leading-snug mb-3">&ldquo;{t.quote}&rdquo;</p>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed mb-4">{t.body}</p>
                <p className="text-sm font-semibold text-[var(--text-on-light)]">{t.name}</p>
                <p className="text-xs text-[var(--text-on-light-faint)]">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────── */}
      <section className="bg-[var(--bg-page)] py-16 md:py-20 px-5">
        <div className="mx-auto max-w-2xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-brand)]">Common questions</h2>
          </div>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, idx) => (
              <div key={idx} className="rounded-2xl bg-[var(--bg-card)] border border-[var(--border-on-light)] shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left font-semibold text-[var(--text-brand)] text-sm md:text-base hover:bg-[var(--bg-surface-dk)] transition-colors"
                >
                  {item.q}
                  <span className="text-[var(--text-brand)] shrink-0 text-lg leading-none">{openFaq === idx ? "−" : "+"}</span>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5 text-sm text-[var(--text-on-light-muted)] leading-relaxed border-t border-[var(--border-on-light)] pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STICKY MOBILE CTA ─────────────────────────────────────────── */}
      <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t border-[var(--border-on-light)] bg-[var(--bg-surface)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--bg-surface)]/80">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <span className="text-sm font-medium text-[var(--text-on-light)]">
            {quizStep === "quiz" ? "Skip quiz — book direct." : "Not sure? Start free."}
          </span>
          <Link
            href="/workwithme/discoverycall"
            onClick={() => track("click", { label: `Sticky Mobile CTA - Book Free Call${quizStep === "quiz" ? " (skip quiz)" : ""}`, component: "WorkWithMePage" }, { event: "click", label: `Sticky Mobile CTA - Book Free Call${quizStep === "quiz" ? " (skip quiz)" : ""}`, component: "WorkWithMePage" })}
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-brand)] text-[var(--text-on-dark)] font-bold text-sm px-5 py-2.5 shadow hover:opacity-90 transition-all"
          >
            Book Free Call
          </Link>
        </div>
      </div>

      {/* ── FINAL CTA ─────────────────────────────────────────────────── */}
      <section className="bg-[var(--color-brand)] py-16 md:py-20 pb-28 md:pb-20 px-5 text-center">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[var(--text-on-dark)] mb-4">
            Ready to feel like yourself again?
          </h2>
          <p className="text-[var(--text-on-dark-muted)] text-base mb-8 max-w-md mx-auto">
            Start with a free 15-minute discovery call — no pressure, just clarity on your next step.
          </p>
          <Link
            href="/workwithme/discoverycall"
            onClick={() => track("click", { label: "Footer CTA - Book Free Call", component: "WorkWithMePage" }, { event: "click", label: "Footer CTA - Book Free Call", component: "WorkWithMePage" })}
            className="inline-flex items-center justify-center bg-[var(--btn-inverse-bg)] text-[var(--btn-inverse-text)] font-bold rounded-full px-10 py-4 hover:bg-[var(--btn-inverse-hover)] shadow-lg transition-all hover:scale-[1.02]"
          >
            Book Your Free Call →
          </Link>
          <p className="text-[var(--text-on-dark-faint)] text-xs mt-6">
            Face to face in Manchester (Wed) or Southport (Mon &amp; Thu), or online.
          </p>
        </div>
      </section>

    </div>
  );
}
